import Phaser from 'phaser';
import type { AlchemyType, Genome } from '@/domain/genetics/types';
import { abilityForType } from '@/domain/combat/abilities';
import { deriveCombatVisualProfile } from '@/domain/combat/combat-visual-profile';
import { buildGenomeCombatEffectRenderer } from './GenomeCombatEffectRenderer';

/**
 * Efeitos rápidos (hitbox visual, dano flutuante, skill burst).
 */
export interface EffectsApi {
  flashHitbox(x: number, y: number, w: number, h: number): void;
  popDamage(x: number, y: number, dmg: number, crit: boolean): void;
  skillBurst(x: number, y: number, type: AlchemyType, genome?: Genome): void;
}

const TYPE_COLOR: Record<AlchemyType, number> = {
  Fire: 0xff704d, Water: 0x5ac8fa, Earth: 0xd4a76a,
  Air: 0xe5fbff, Life: 0x7ee787, Shadow: 0xb187ff,
};

export function buildEffects(scene: Phaser.Scene, options: { reducedMotion?: boolean } = {}): EffectsApi {
  const genomeEffects = buildGenomeCombatEffectRenderer(scene, options);
  return {
    flashHitbox(x, y, w, h) {
      const rect = scene.add.rectangle(x, y, w, h, 0xffffff, 0.6).setDepth(20);
      scene.tweens.add({
        targets: rect,
        alpha: 0,
        duration: 110,
        onComplete: () => rect.destroy(),
      });
    },
    popDamage(x, y, dmg, crit) {
      const text = scene.add
        .text(x, y, `${dmg}`, {
          fontFamily: 'monospace',
          fontSize: crit ? '18px' : '14px',
          color: crit ? '#ffe066' : '#ffffff',
          stroke: '#000',
          strokeThickness: 2,
          resolution: 2,
        })
        .setOrigin(0.5)
        .setDepth(25);
      if (options.reducedMotion) scene.time.delayedCall(500, () => text.destroy());
      else scene.tweens.add({
          targets: text,
          y: y - 24,
          alpha: 0,
          duration: 700,
          onComplete: () => text.destroy(),
        });
    },
    skillBurst(x, y, type, genome) {
      const effect = abilityForType(type).effect;
      const radius = effect.kind === 'wave' ? effect.range : effect.kind === 'dash' ? effect.distance : effect.radius;
      if (genome) {
        genomeEffects.burst(x, y, radius, deriveCombatVisualProfile(genome));
        return;
      }
      const c = scene.add.circle(x, y, options.reducedMotion ? radius : 4, TYPE_COLOR[type], 0.22)
        .setStrokeStyle(2, TYPE_COLOR[type], 0.95)
        .setDepth(15);
      if (options.reducedMotion) {
        scene.time.delayedCall(220, () => c.destroy());
        return;
      }
      scene.tweens.add({
        targets: c,
        radius,
        alpha: 0,
        duration: 280,
        onUpdate: (tw) => {
          const v = (tw.getValue() ?? 0) as number;
          c.setRadius(v);
        },
        onComplete: () => c.destroy(),
      });
    },
  };
}
